import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  PhotoIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PlayCircleIcon,
  DocumentTextIcon,
  DocumentIcon,
  PresentationChartBarIcon,
  FolderArrowDownIcon,
  QuestionMarkCircleIcon,
  CloudArrowUpIcon,
  StarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { Card, Badge, InlineSpinner, Button, Modal, ModalBody, ModalFooter } from '@/components/ui'
import { lmsApi } from '@/api'
import type {
  AdminCourseDetail,
  AdminModuleNested,
  AdminLessonNested,
  CreateModuleData,
  CreateLessonData,
  LessonType,
  AdminLesson,
  AdminQuiz,
  AdminQuestion,
  AdminAnswer,
  CreateQuizData,
  CreateQuestionData,
  AdminQuizAttempts,
} from '@/api/lms'

// Same limit applied by the backend (LMS_MAX_UPLOAD_MB)
const MAX_LESSON_FILE_MB = 512

const lessonTypeConfig: Record<LessonType, { label: string; icon: typeof PlayCircleIcon; color: string }> = {
  video: { label: 'Video', icon: PlayCircleIcon, color: 'text-red-500' },
  pdf: { label: 'PDF', icon: DocumentTextIcon, color: 'text-blue-500' },
  html: { label: 'Articolo', icon: DocumentIcon, color: 'text-green-500' },
  presentation: { label: 'Presentazione', icon: PresentationChartBarIcon, color: 'text-orange-500' },
  resource: { label: 'Risorsa', icon: FolderArrowDownIcon, color: 'text-purple-500' },
  scorm: { label: 'SCORM', icon: CloudArrowUpIcon, color: 'text-cyan-500' },
  quiz: { label: 'Quiz', icon: QuestionMarkCircleIcon, color: 'text-yellow-500' },
}

export default function AdminCourseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [course, setCourse] = useState<AdminCourseDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set())
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false)

  // Module Modal
  const [showModuleModal, setShowModuleModal] = useState(false)
  const [editingModule, setEditingModule] = useState<AdminModuleNested | null>(null)
  const [moduleForm, setModuleForm] = useState<CreateModuleData>({ course: 0, title: '', description: '' })
  const [savingModule, setSavingModule] = useState(false)

  // Lesson Modal
  const [showLessonModal, setShowLessonModal] = useState(false)
  const [editingLesson, setEditingLesson] = useState<AdminLesson | null>(null)
  const [currentModuleId, setCurrentModuleId] = useState<number | null>(null)
  const [lessonForm, setLessonForm] = useState<CreateLessonData>({
    module: 0,
    title: '',
    type: 'video',
    content: '',
    video_url: '',
    duration_minutes: 0,
    is_free_preview: false,
  })
  const [savingLesson, setSavingLesson] = useState(false)
  const [savingSequential, setSavingSequential] = useState(false)

  // Quiz attempts modal
  const [showAttemptsModal, setShowAttemptsModal] = useState(false)
  const [attemptsTitle, setAttemptsTitle] = useState('')
  const [attempts, setAttempts] = useState<AdminQuizAttempts | null>(null)
  const [loadingAttempts, setLoadingAttempts] = useState(false)
  const [attemptsError, setAttemptsError] = useState<string | null>(null)
  const [confirmResetId, setConfirmResetId] = useState<number | null>(null)
  const [resettingId, setResettingId] = useState<number | null>(null)
  const [lessonError, setLessonError] = useState<string | null>(null)
  const [lessonFile, setLessonFile] = useState<File | null>(null)

  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteType, setDeleteType] = useState<'module' | 'lesson'>('module')
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; title: string } | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Quiz Editor Modal
  const [showQuizModal, setShowQuizModal] = useState(false)
  const [quizLessonId, setQuizLessonId] = useState<number | null>(null)
  const [quiz, setQuiz] = useState<AdminQuiz | null>(null)
  const [loadingQuiz, setLoadingQuiz] = useState(false)
  const [savingQuiz, setSavingQuiz] = useState(false)
  const [quizForm, setQuizForm] = useState<CreateQuizData>({
    lesson: 0,
    title: '',
    description: '',
    passing_score: 60,
    time_limit_minutes: null,
    max_attempts: 3,
    shuffle_questions: false,
    show_correct_answers: true,
  })

  // Question Editor
  const [editingQuestion, setEditingQuestion] = useState<AdminQuestion | null>(null)
  const [showQuestionModal, setShowQuestionModal] = useState(false)
  const [questionForm, setQuestionForm] = useState<CreateQuestionData>({
    quiz: 0,
    text: '',
    type: 'single',
    points: 1,
    explanation: '',
  })
  const [savingQuestion, setSavingQuestion] = useState(false)
  const [currentQuestionId, setCurrentQuestionId] = useState<number | null>(null)

  // Answer Editor (inline in question modal)
  const [questionAnswers, setQuestionAnswers] = useState<AdminAnswer[]>([])
  const [newAnswerText, setNewAnswerText] = useState('')
  const [newAnswerCorrect, setNewAnswerCorrect] = useState(false)
  const [savingAnswer, setSavingAnswer] = useState(false)

  // Reviews
  const [reviews, setReviews] = useState<{
    count: number
    average_rating: number | null
    results: Array<{
      id: number
      user_id: number
      user_name: string
      user_email: string
      rating: number
      review: string
      rated_at: string
      completed_at: string | null
    }>
  } | null>(null)
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [showReviews, setShowReviews] = useState(false)

  useEffect(() => {
    if (id) {
      fetchCourseDetail()
    }
  }, [id])

  const fetchCourseDetail = async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await lmsApi.admin.getCourseDetail(parseInt(id))
      setCourse(data)
      // Auto-expand first module
      if (data.modules.length > 0) {
        setExpandedModules(new Set([data.modules[0].id]))
      }
    } catch (error) {
      console.error('Error fetching course:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchReviews = async () => {
    if (!id) return
    setLoadingReviews(true)
    try {
      const data = await lmsApi.admin.getCourseReviews(parseInt(id))
      setReviews(data)
    } catch (error) {
      console.error('Error fetching reviews:', error)
    } finally {
      setLoadingReviews(false)
    }
  }

  const handleToggleReviews = () => {
    if (!showReviews && !reviews) {
      fetchReviews()
    }
    setShowReviews(!showReviews)
  }

  const toggleModule = (moduleId: number) => {
    setExpandedModules(prev => {
      const next = new Set(prev)
      if (next.has(moduleId)) {
        next.delete(moduleId)
      } else {
        next.add(moduleId)
      }
      return next
    })
  }

  // Thumbnail upload
  const handleThumbnailClick = () => {
    fileInputRef.current?.click()
  }

  const handleThumbnailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !course) return

    setUploadingThumbnail(true)
    try {
      await lmsApi.admin.uploadCourseThumbnail(course.id, file)
      fetchCourseDetail()
    } catch (error) {
      console.error('Error uploading thumbnail:', error)
    } finally {
      setUploadingThumbnail(false)
    }
  }

  // Module CRUD
  const handleOpenCreateModule = () => {
    if (!course) return
    setEditingModule(null)
    setModuleForm({
      course: course.id,
      title: '',
      description: '',
      order: course.modules.length,
    })
    setShowModuleModal(true)
  }

  const handleOpenEditModule = (module: AdminModuleNested) => {
    if (!course) return
    setEditingModule(module)
    setModuleForm({
      course: course.id,
      title: module.title,
      description: module.description,
      order: module.order,
    })
    setShowModuleModal(true)
  }

  const handleSaveModule = async () => {
    if (!moduleForm.title.trim()) return

    setSavingModule(true)
    try {
      if (editingModule) {
        await lmsApi.admin.updateModule(editingModule.id, moduleForm)
      } else {
        await lmsApi.admin.createModule(moduleForm)
      }
      setShowModuleModal(false)
      fetchCourseDetail()
    } catch (error) {
      console.error('Error saving module:', error)
    } finally {
      setSavingModule(false)
    }
  }

  const handleDeleteModule = (module: AdminModuleNested) => {
    setDeleteType('module')
    setDeleteTarget({ id: module.id, title: module.title })
    setShowDeleteModal(true)
  }

  const handleToggleSequential = async (sequential: boolean) => {
    if (!course) return
    setSavingSequential(true)
    setCourse({ ...course, sequential_modules: sequential })
    try {
      await lmsApi.admin.updateCourse(course.id, { sequential_modules: sequential })
    } catch (error) {
      console.error('Error updating course sequence:', error)
      setCourse((current) => (current ? { ...current, sequential_modules: !sequential } : current))
    } finally {
      setSavingSequential(false)
    }
  }

  // Quiz attempts
  const handleOpenAttempts = async (lesson: AdminLessonNested) => {
    setAttemptsTitle(lesson.title)
    setAttempts(null)
    setAttemptsError(null)
    setConfirmResetId(null)
    setShowAttemptsModal(true)
    setLoadingAttempts(true)
    try {
      const quizzes = await lmsApi.admin.getQuizzes(lesson.id)
      if (quizzes.length === 0) {
        setAttemptsError('Il quiz non è ancora stato configurato.')
        return
      }
      setAttempts(await lmsApi.admin.getQuizAttempts(quizzes[0].id))
    } catch (error: any) {
      console.error('Error fetching quiz attempts:', error)
      setAttemptsError(error.response?.data?.error || 'Errore nel caricamento dei tentativi')
    } finally {
      setLoadingAttempts(false)
    }
  }

  const handleResetAttempts = async (enrollmentId: number) => {
    if (!attempts) return
    setResettingId(enrollmentId)
    setAttemptsError(null)
    try {
      await lmsApi.admin.resetQuizAttempts(attempts.quiz_id, enrollmentId)
      setAttempts(await lmsApi.admin.getQuizAttempts(attempts.quiz_id))
      setConfirmResetId(null)
    } catch (error: any) {
      console.error('Error resetting quiz attempts:', error)
      setAttemptsError(error.response?.data?.error || 'Errore nell\'azzeramento dei tentativi')
    } finally {
      setResettingId(null)
    }
  }

  // Lesson CRUD
  const handleOpenCreateLesson = (moduleId: number) => {
    setEditingLesson(null)
    setCurrentModuleId(moduleId)
    setLessonForm({
      module: moduleId,
      title: '',
      type: 'video',
      content: '',
      video_url: '',
      duration_minutes: 0,
      is_free_preview: false,
      // In a sequential course videos are mandatory by default
      require_full_view: !!course?.sequential_modules,
    })
    setLessonFile(null)
    setLessonError(null)
    setShowLessonModal(true)
  }

  const handleOpenEditLesson = async (lesson: AdminLessonNested, moduleId: number) => {
    setCurrentModuleId(moduleId)
    try {
      const fullLesson = await lmsApi.admin.getLesson(lesson.id)
      setEditingLesson(fullLesson)
      setLessonForm({
        module: moduleId,
        title: fullLesson.title,
        type: fullLesson.type,
        content: fullLesson.content || '',
        video_url: fullLesson.video_url || '',
        duration_minutes: fullLesson.duration_minutes,
        is_free_preview: fullLesson.is_free_preview,
        require_full_view: fullLesson.require_full_view,
      })
      setLessonFile(null)
      setLessonError(null)
      setShowLessonModal(true)
    } catch (error) {
      console.error('Error fetching lesson:', error)
    }
  }

  const handleSaveLesson = async () => {
    if (!lessonForm.title.trim()) return

    if (lessonFile && lessonFile.size > MAX_LESSON_FILE_MB * 1024 * 1024) {
      const sizeMb = Math.ceil(lessonFile.size / 1024 / 1024)
      setLessonError(`Il file è di ${sizeMb} MB e supera il limite di ${MAX_LESSON_FILE_MB} MB.`)
      return
    }

    const requireFullView = lessonForm.type === 'video' && !!lessonForm.require_full_view
    if (requireFullView && lessonForm.video_url) {
      setLessonError('La visione obbligatoria richiede un file MP4 caricato: rimuovi l\'URL del video.')
      return
    }
    if (requireFullView && !lessonFile && !editingLesson?.file_url) {
      setLessonError('Carica il file MP4 del video per attivare la visione obbligatoria.')
      return
    }

    setSavingLesson(true)
    setLessonError(null)
    const moduleIdToKeepExpanded = currentModuleId
    try {
      let lessonId: number
      const lessonData = { ...lessonForm, require_full_view: requireFullView }

      if (editingLesson) {
        await lmsApi.admin.updateLesson(editingLesson.id, lessonData)
        lessonId = editingLesson.id
      } else {
        const newLesson = await lmsApi.admin.createLesson(lessonData)
        lessonId = newLesson.id
        // If the file upload fails, saving again updates this lesson
        setEditingLesson(newLesson)
      }

      // Upload file if provided
      if (lessonFile) {
        await lmsApi.admin.uploadLessonFile(lessonId, lessonFile)
      }

      setShowLessonModal(false)

      // Fetch updated data but preserve expanded state
      if (!id) return
      const data = await lmsApi.admin.getCourseDetail(parseInt(id))
      setCourse(data)

      // Keep the current module expanded after saving
      if (moduleIdToKeepExpanded) {
        setExpandedModules(prev => new Set([...prev, moduleIdToKeepExpanded]))
      }
    } catch (error: any) {
      console.error('Error saving lesson:', error)
      const data = error.response?.data
      setLessonError(
        error.response?.status === 413
          ? `Il file supera il limite di ${MAX_LESSON_FILE_MB} MB.`
          : data?.error ||
            data?.require_full_view?.[0] ||
            data?.detail ||
            'Errore nel salvataggio della lezione'
      )
      if (id) {
        lmsApi.admin.getCourseDetail(parseInt(id)).then(setCourse).catch(() => undefined)
      }
    } finally {
      setSavingLesson(false)
    }
  }

  const handleDeleteLesson = (lesson: AdminLessonNested) => {
    setDeleteType('lesson')
    setDeleteTarget({ id: lesson.id, title: lesson.title })
    setShowDeleteModal(true)
  }

  // Delete handler
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return

    setDeleting(true)
    try {
      if (deleteType === 'module') {
        await lmsApi.admin.deleteModule(deleteTarget.id)
      } else {
        await lmsApi.admin.deleteLesson(deleteTarget.id)
      }
      setShowDeleteModal(false)
      setDeleteTarget(null)
      fetchCourseDetail()
    } catch (error) {
      console.error('Error deleting:', error)
    } finally {
      setDeleting(false)
    }
  }

  // Quiz Editor handlers
  const handleOpenQuizEditor = async (lesson: AdminLessonNested) => {
    setQuizLessonId(lesson.id)
    setLoadingQuiz(true)
    setShowQuizModal(true)

    try {
      // Try to get existing quiz for this lesson
      const quizzes = await lmsApi.admin.getQuizzes(lesson.id)
      if (quizzes.length > 0) {
        const existingQuiz = await lmsApi.admin.getQuiz(quizzes[0].id)
        setQuiz(existingQuiz)
        setQuizForm({
          lesson: lesson.id,
          title: existingQuiz.title,
          description: existingQuiz.description,
          passing_score: existingQuiz.passing_score,
          time_limit_minutes: existingQuiz.time_limit_minutes,
          max_attempts: existingQuiz.max_attempts,
          shuffle_questions: existingQuiz.shuffle_questions,
          show_correct_answers: existingQuiz.show_correct_answers,
        })
      } else {
        setQuiz(null)
        setQuizForm({
          lesson: lesson.id,
          title: `Quiz - ${lesson.title}`,
          description: '',
          passing_score: 60,
          time_limit_minutes: null,
          max_attempts: 3,
          shuffle_questions: false,
          show_correct_answers: true,
        })
      }
    } catch (error) {
      console.error('Error loading quiz:', error)
      setQuiz(null)
      setQuizForm({
        lesson: lesson.id,
        title: `Quiz - ${lesson.title}`,
        description: '',
        passing_score: 60,
        time_limit_minutes: null,
        max_attempts: 3,
        shuffle_questions: false,
        show_correct_answers: true,
      })
    } finally {
      setLoadingQuiz(false)
    }
  }

  const handleSaveQuiz = async () => {
    if (!quizLessonId) return

    setSavingQuiz(true)
    try {
      if (quiz) {
        const updatedQuiz = await lmsApi.admin.updateQuiz(quiz.id, quizForm)
        setQuiz({ ...quiz, ...updatedQuiz })
      } else {
        const newQuiz = await lmsApi.admin.createQuiz(quizForm)
        // Fetch full quiz with questions
        const fullQuiz = await lmsApi.admin.getQuiz(newQuiz.id)
        setQuiz(fullQuiz)
      }
    } catch (error) {
      console.error('Error saving quiz:', error)
    } finally {
      setSavingQuiz(false)
    }
  }

  const handleDeleteQuiz = async () => {
    if (!quiz) return

    if (!confirm('Sei sicuro di voler eliminare questo quiz e tutte le domande?')) return

    try {
      await lmsApi.admin.deleteQuiz(quiz.id)
      setQuiz(null)
      setShowQuizModal(false)
      fetchCourseDetail()
    } catch (error) {
      console.error('Error deleting quiz:', error)
    }
  }

  // Question handlers
  const handleOpenCreateQuestion = () => {
    if (!quiz) return
    setEditingQuestion(null)
    setCurrentQuestionId(null)
    setQuestionAnswers([])
    setNewAnswerText('')
    setNewAnswerCorrect(false)
    setQuestionForm({
      quiz: quiz.id,
      text: '',
      type: 'single',
      points: 1,
      explanation: '',
    })
    setShowQuestionModal(true)
  }

  const handleOpenEditQuestion = (question: AdminQuestion) => {
    setEditingQuestion(question)
    setCurrentQuestionId(question.id)
    setQuestionAnswers(question.answers || [])
    setNewAnswerText('')
    setNewAnswerCorrect(false)
    setQuestionForm({
      quiz: question.quiz,
      text: question.text,
      type: question.type,
      points: question.points,
      explanation: question.explanation,
    })
    setShowQuestionModal(true)
  }

  const handleSaveQuestion = async () => {
    if (!questionForm.text.trim() || !quiz) return

    setSavingQuestion(true)
    try {
      let questionId: number
      if (editingQuestion) {
        await lmsApi.admin.updateQuestion(editingQuestion.id, questionForm)
        questionId = editingQuestion.id
      } else {
        const newQuestion = await lmsApi.admin.createQuestion(questionForm)
        questionId = newQuestion.id
        setCurrentQuestionId(questionId)
        setEditingQuestion(newQuestion)
      }
      // Refresh quiz data but DON'T close modal - let user add answers
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
      // Update local answers from refreshed data
      const updatedQuestion = updatedQuiz.questions?.find(q => q.id === questionId)
      if (updatedQuestion) {
        setQuestionAnswers(updatedQuestion.answers || [])
      }
    } catch (error) {
      console.error('Error saving question:', error)
    } finally {
      setSavingQuestion(false)
    }
  }

  const handleCloseQuestionModal = async () => {
    setShowQuestionModal(false)
    // Refresh quiz when closing
    if (quiz) {
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    }
  }

  const handleDeleteQuestion = async (questionId: number) => {
    if (!quiz) return
    if (!confirm('Sei sicuro di voler eliminare questa domanda?')) return

    try {
      await lmsApi.admin.deleteQuestion(questionId)
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error deleting question:', error)
    }
  }

  // Answer handlers for question modal
  const handleAddAnswerInModal = async () => {
    if (!newAnswerText.trim() || !currentQuestionId || !quiz) return

    setSavingAnswer(true)
    try {
      await lmsApi.admin.createAnswer({
        question: currentQuestionId,
        text: newAnswerText,
        is_correct: newAnswerCorrect,
      })
      setNewAnswerText('')
      setNewAnswerCorrect(false)
      // Refresh answers
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
      const updatedQuestion = updatedQuiz.questions?.find(q => q.id === currentQuestionId)
      if (updatedQuestion) {
        setQuestionAnswers(updatedQuestion.answers || [])
      }
    } catch (error) {
      console.error('Error adding answer:', error)
    } finally {
      setSavingAnswer(false)
    }
  }

  const handleToggleAnswerCorrectInModal = async (answer: AdminAnswer) => {
    if (!quiz || !currentQuestionId) return

    try {
      await lmsApi.admin.updateAnswer(answer.id, { is_correct: !answer.is_correct })
      // Update local state immediately for better UX
      setQuestionAnswers(prev => prev.map(a =>
        a.id === answer.id ? { ...a, is_correct: !a.is_correct } : a
      ))
      // Also refresh quiz
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error updating answer:', error)
    }
  }

  const handleDeleteAnswerInModal = async (answerId: number) => {
    if (!quiz || !currentQuestionId) return

    try {
      await lmsApi.admin.deleteAnswer(answerId)
      setQuestionAnswers(prev => prev.filter(a => a.id !== answerId))
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error deleting answer:', error)
    }
  }

  // Answer handlers for quiz overview (keep for backward compatibility)
  const handleAddAnswer = async (questionId: number) => {
    if (!newAnswerText.trim() || !quiz) return

    setSavingAnswer(true)
    try {
      await lmsApi.admin.createAnswer({
        question: questionId,
        text: newAnswerText,
        is_correct: false,
      })
      setNewAnswerText('')
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error adding answer:', error)
    } finally {
      setSavingAnswer(false)
    }
  }

  const handleToggleCorrectAnswer = async (answer: AdminAnswer) => {
    if (!quiz) return

    try {
      await lmsApi.admin.updateAnswer(answer.id, { is_correct: !answer.is_correct })
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error updating answer:', error)
    }
  }

  const handleDeleteAnswer = async (answerId: number) => {
    if (!quiz) return

    try {
      await lmsApi.admin.deleteAnswer(answerId)
      const updatedQuiz = await lmsApi.admin.getQuiz(quiz.id)
      setQuiz(updatedQuiz)
    } catch (error) {
      console.error('Error deleting answer:', error)
    }
  }

  if (loading) {
    return <InlineSpinner message="Caricamento corso..." />
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Corso non trovato</p>
        <Button variant="ghost" onClick={() => navigate('/gestione/corsi')} className="mt-4">
          Torna alla lista
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/gestione/corsi')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{course.title}</h1>
          <p className="text-gray-500">
            {course.modules_count} moduli, {course.lessons_count} lezioni, {course.enrolled_count} iscritti
          </p>
        </div>
        <Badge variant={course.is_published ? 'success' : 'default'}>
          {course.is_published ? 'Pubblicato' : 'Bozza'}
        </Badge>
      </div>

      {/* Course Info Card */}
      <Card className="p-6">
        <div className="flex items-start gap-6">
          {/* Thumbnail */}
          <div
            onClick={handleThumbnailClick}
            className="relative w-48 h-32 bg-gray-100 rounded-lg overflow-hidden cursor-pointer group"
          >
            {course.thumbnail_url ? (
              <img
                src={course.thumbnail_url}
                alt={course.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <PhotoIcon className="h-12 w-12 text-gray-300" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              {uploadingThumbnail ? (
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
              ) : (
                <PhotoIcon className="h-8 w-8 text-white" />
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="hidden"
            />
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Categoria</p>
                <p className="font-medium">{course.category_name || 'Nessuna'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Livello</p>
                <p className="font-medium capitalize">{course.level}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Durata</p>
                <p className="font-medium">{course.duration_hours} ore</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Certificato</p>
                <p className="font-medium">{course.has_certificate ? 'Si' : 'No'}</p>
              </div>
            </div>
            {course.short_description && (
              <p className="mt-4 text-gray-600">{course.short_description}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Sequential path */}
      <Card className="p-4">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={!!course.sequential_modules}
            disabled={savingSequential}
            onChange={(e) => handleToggleSequential(e.target.checked)}
            className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <span>
            <span className="block font-medium text-gray-900">Moduli sequenziali</span>
            <span className="block text-sm text-gray-500">
              Ogni modulo è propedeutico al successivo: si sblocca solo dopo aver completato tutte
              le lezioni del modulo precedente (video visti fino alla fine e questionari superati).
            </span>
          </span>
        </label>
      </Card>

      {/* Modules Section */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Moduli e Lezioni</h2>
        <Button onClick={handleOpenCreateModule}>
          <PlusIcon className="h-5 w-5 mr-2" />
          Nuovo Modulo
        </Button>
      </div>

      {course.modules.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-gray-500 mb-4">Nessun modulo creato</p>
          <Button onClick={handleOpenCreateModule}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Crea il primo modulo
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {course.modules.map((module, moduleIndex) => (
            <Card key={module.id} className="overflow-hidden">
              {/* Module Header */}
              <div
                className="flex items-center gap-4 p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleModule(module.id)}
              >
                <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-600 rounded-full font-semibold text-sm">
                  {moduleIndex + 1}
                </span>
                <div className="flex-1">
                  <h3 className="font-medium">{module.title}</h3>
                  <p className="text-sm text-gray-500">
                    {module.lessons_count} lezioni
                    {module.description && ` - ${module.description}`}
                  </p>
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => handleOpenCreateLesson(module.id)}
                    className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                    title="Aggiungi lezione"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleOpenEditModule(module)}
                    className="p-1 text-gray-500 hover:bg-gray-200 rounded"
                    title="Modifica modulo"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteModule(module)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                    title="Elimina modulo"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
                {expandedModules.has(module.id) ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                )}
              </div>

              {/* Lessons List */}
              {expandedModules.has(module.id) && (
                <div className="border-t border-gray-200">
                  {module.lessons.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      <p>Nessuna lezione</p>
                      <button
                        onClick={() => handleOpenCreateLesson(module.id)}
                        className="mt-2 text-primary-600 hover:underline"
                      >
                        + Aggiungi prima lezione
                      </button>
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {module.lessons.map((lesson, lessonIndex) => {
                        const config = lessonTypeConfig[lesson.type] || lessonTypeConfig.video
                        const Icon = config.icon
                        return (
                          <li
                            key={lesson.id}
                            className="flex items-center gap-4 p-4 hover:bg-gray-50"
                          >
                            <span className="text-sm text-gray-400 w-6">
                              {lessonIndex + 1}.
                            </span>
                            <Icon className={`h-5 w-5 ${config.color}`} />
                            <div className="flex-1">
                              <p className="font-medium">{lesson.title}</p>
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <span>{config.label}</span>
                                {lesson.duration_minutes > 0 && (
                                  <span>- {lesson.duration_minutes} min</span>
                                )}
                                {lesson.is_free_preview && (
                                  <Badge variant="info" size="sm">Anteprima</Badge>
                                )}
                                {lesson.require_full_view && (
                                  <Badge variant="warning" size="sm">Visione obbligatoria</Badge>
                                )}
                                {lesson.has_quiz && (
                                  <Badge variant="warning" size="sm">Quiz</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {lesson.type === 'quiz' && (
                                <button
                                  onClick={() => handleOpenAttempts(lesson)}
                                  className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                  title="Tentativi degli utenti"
                                >
                                  <UserGroupIcon className="h-4 w-4" />
                                </button>
                              )}
                              {lesson.type === 'quiz' && (
                                <button
                                  onClick={() => handleOpenQuizEditor(lesson)}
                                  className="p-1 text-yellow-500 hover:bg-yellow-50 rounded"
                                  title="Gestisci Quiz"
                                >
                                  <QuestionMarkCircleIcon className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleOpenEditLesson(lesson, module.id)}
                                className="p-1 text-gray-500 hover:bg-gray-200 rounded"
                                title="Modifica"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteLesson(lesson)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                title="Elimina"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Reviews Section */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-yellow-500" />
            Recensioni
            {reviews && (
              <Badge variant="info">{reviews.count}</Badge>
            )}
          </h2>
          <Button variant="outline" onClick={handleToggleReviews}>
            {showReviews ? 'Nascondi' : 'Mostra recensioni'}
          </Button>
        </div>

        {showReviews && (
          <Card>
            {loadingReviews ? (
              <div className="py-8">
                <InlineSpinner message="Caricamento recensioni..." />
              </div>
            ) : reviews && reviews.results.length > 0 ? (
              <div className="space-y-4">
                {/* Summary */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-gray-900">
                      {reviews.average_rating?.toFixed(1) || '-'}
                    </div>
                    <div className="flex items-center justify-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIconSolid
                          key={star}
                          className={`h-4 w-4 ${
                            star <= (reviews.average_rating || 0)
                              ? 'text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="text-sm text-gray-600">
                    <strong>{reviews.count}</strong> recensioni totali
                  </div>
                </div>

                {/* Reviews List */}
                <div className="divide-y divide-gray-200">
                  {reviews.results.map((review) => (
                    <div key={review.id} className="py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{review.user_name}</p>
                          <p className="text-sm text-gray-500">{review.user_email}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <StarIconSolid
                                key={star}
                                className={`h-4 w-4 ${
                                  star <= review.rating
                                    ? 'text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                          {review.rated_at && (
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(review.rated_at).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>
                      </div>
                      {review.review && (
                        <p className="mt-2 text-gray-700">{review.review}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                <StarIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>Nessuna recensione per questo corso</p>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Module Modal */}
      <Modal
        isOpen={showModuleModal}
        onClose={() => setShowModuleModal(false)}
        title={editingModule ? 'Modifica Modulo' : 'Nuovo Modulo'}
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={moduleForm.title}
                onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. Modulo 1: Introduzione"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                value={moduleForm.description || ''}
                onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Descrizione del modulo..."
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowModuleModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleSaveModule} disabled={savingModule || !moduleForm.title.trim()}>
            {savingModule ? 'Salvataggio...' : editingModule ? 'Salva' : 'Crea'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Lesson Modal */}
      <Modal
        isOpen={showLessonModal}
        onClose={() => setShowLessonModal(false)}
        title={editingLesson ? 'Modifica Lezione' : 'Nuova Lezione'}
        size="lg"
      >
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titolo *
              </label>
              <input
                type="text"
                value={lessonForm.title}
                onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                placeholder="es. Introduzione al corso"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo *
                </label>
                <select
                  value={lessonForm.type}
                  onChange={(e) => setLessonForm({ ...lessonForm, type: e.target.value as LessonType })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {Object.entries(lessonTypeConfig).map(([value, config]) => (
                    <option key={value} value={value}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durata (minuti)
                </label>
                <input
                  type="number"
                  min="0"
                  value={lessonForm.duration_minutes || 0}
                  onChange={(e) => setLessonForm({ ...lessonForm, duration_minutes: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Video URL field */}
            {lessonForm.type === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL Video (YouTube, Vimeo, ecc.)
                </label>
                <input
                  type="url"
                  value={lessonForm.video_url || ''}
                  onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Oppure carica un file video MP4 qui sotto
                </p>
              </div>
            )}

            {/* Content field for HTML articles */}
            {lessonForm.type === 'html' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contenuto HTML
                </label>
                <textarea
                  value={lessonForm.content || ''}
                  onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  rows={8}
                  placeholder="<p>Il contenuto della lezione...</p>"
                />
              </div>
            )}

            {/* File upload for video, pdf, presentation, resource, scorm */}
            {['video', 'pdf', 'presentation', 'resource', 'scorm'].includes(lessonForm.type) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {lessonForm.type === 'video' ? 'File Video (MP4, WebM)' :
                   lessonForm.type === 'pdf' ? 'File PDF' :
                   lessonForm.type === 'presentation' ? 'Presentazione (PPTX, PDF)' :
                   lessonForm.type === 'scorm' ? 'Pacchetto SCORM (ZIP)' :
                   'File da scaricare'}
                </label>
                <input
                  type="file"
                  accept={
                    lessonForm.type === 'video' ? '.mp4,.webm,.mov' :
                    lessonForm.type === 'pdf' ? '.pdf' :
                    lessonForm.type === 'presentation' ? '.pptx,.ppt,.pdf' :
                    lessonForm.type === 'scorm' ? '.zip' :
                    '*'
                  }
                  onChange={(e) => setLessonFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Dimensione massima: {MAX_LESSON_FILE_MB} MB
                </p>
                {editingLesson?.file_url && !lessonFile && lessonForm.type !== 'scorm' && (
                  <p className="mt-1 text-sm text-gray-500">
                    File attuale: <a href={editingLesson.file_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">Visualizza</a>
                  </p>
                )}
                {lessonForm.type === 'scorm' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Esporta il contenuto in formato SCORM 1.2 o SCORM 2004 (file ZIP con imsmanifest.xml).
                  </p>
                )}
                {lessonForm.type === 'scorm' && editingLesson?.scorm && !lessonFile && (
                  <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                    Pacchetto attuale: <strong>SCORM {editingLesson.scorm.version}</strong>
                    {editingLesson.scorm.title && <> · {editingLesson.scorm.title}</>}
                    <span className="block text-xs text-gray-500">File di avvio: {editingLesson.scorm.launch}</span>
                    {editingLesson.scorm.sco_count > 1 && (
                      <span className="block text-xs text-amber-700 mt-1">
                        Il pacchetto contiene {editingLesson.scorm.sco_count} parti (SCO): viene avviata solo la prima.
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="pt-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={lessonForm.is_free_preview}
                  onChange={(e) => setLessonForm({ ...lessonForm, is_free_preview: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Anteprima gratuita (visibile senza iscrizione)</span>
              </label>
            </div>

            {lessonForm.type === 'video' && (
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!lessonForm.require_full_view}
                    onChange={(e) => setLessonForm({ ...lessonForm, require_full_view: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Visione completa obbligatoria</span>
                </label>
                <p className="mt-1 ml-6 text-xs text-gray-500">
                  Il video (file MP4 caricato) deve essere visto fino alla fine, senza saltare in avanti.
                  I quiz che seguono nel corso restano bloccati finché il video non è stato completato.
                </p>
              </div>
            )}

            {lessonError && (
              <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{lessonError}</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowLessonModal(false)}>
            Annulla
          </Button>
          <Button onClick={handleSaveLesson} disabled={savingLesson || !lessonForm.title.trim()}>
            {savingLesson ? 'Salvataggio...' : editingLesson ? 'Salva' : 'Crea'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Elimina ${deleteType === 'module' ? 'Modulo' : 'Lezione'}`}
      >
        <ModalBody>
          {deleteTarget && (
            <div className="space-y-4">
              <p className="text-gray-600">
                Sei sicuro di voler eliminare {deleteType === 'module' ? 'il modulo' : 'la lezione'}{' '}
                <span className="font-semibold">"{deleteTarget.title}"</span>?
              </p>
              {deleteType === 'module' && (
                <p className="text-sm text-red-600">
                  Attenzione: verranno eliminate anche tutte le lezioni contenute nel modulo.
                </p>
              )}
              <p className="text-sm text-gray-500">
                Questa azione è irreversibile.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Quiz Attempts Modal */}
      <Modal
        isOpen={showAttemptsModal}
        onClose={() => setShowAttemptsModal(false)}
        title={`Tentativi - ${attemptsTitle}`}
        size="xl"
      >
        <ModalBody>
          {loadingAttempts ? (
            <div className="py-8 text-center">
              <InlineSpinner message="Caricamento tentativi..." />
            </div>
          ) : (
            <div className="space-y-4">
              {attemptsError && (
                <p className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{attemptsError}</p>
              )}
              {attempts && attempts.results.length === 0 && (
                <p className="py-6 text-center text-gray-500">
                  Nessun utente ha ancora provato questo quiz.
                </p>
              )}
              {attempts && attempts.results.length > 0 && (
                <>
                  <p className="text-sm text-gray-500">
                    Tentativi massimi per utente: {attempts.max_attempts}. Azzerando i tentativi
                    l'utente può riprovare il quiz: i tentativi precedenti restano nello storico
                    ma non vengono più conteggiati.
                  </p>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead>
                        <tr className="text-left text-gray-500">
                          <th className="py-2 pr-4 font-medium">Utente</th>
                          <th className="py-2 pr-4 font-medium">Tentativi</th>
                          <th className="py-2 pr-4 font-medium">Miglior punteggio</th>
                          <th className="py-2 pr-4 font-medium">Esito</th>
                          <th className="py-2 font-medium text-right">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {attempts.results.map((row) => (
                          <tr key={row.enrollment_id}>
                            <td className="py-3 pr-4">
                              <p className="font-medium text-gray-900">{row.user_name}</p>
                              <p className="text-gray-500">{row.user_email}</p>
                            </td>
                            <td className="py-3 pr-4">
                              {row.attempts_used} / {attempts.max_attempts}
                              {row.attempts_reset > 0 && (
                                <span className="block text-xs text-gray-400">
                                  {row.attempts_reset} azzerati
                                </span>
                              )}
                            </td>
                            <td className="py-3 pr-4">
                              {row.best_score !== null ? `${row.best_score}%` : '-'}
                            </td>
                            <td className="py-3 pr-4">
                              {row.passed ? (
                                <Badge variant="success" size="sm">Superato</Badge>
                              ) : row.attempts_remaining === 0 ? (
                                <Badge variant="danger" size="sm">Tentativi esauriti</Badge>
                              ) : (
                                <Badge variant="warning" size="sm">Non superato</Badge>
                              )}
                            </td>
                            <td className="py-3 text-right whitespace-nowrap">
                              {row.passed || row.attempts_used === 0 ? (
                                <span className="text-gray-400">-</span>
                              ) : confirmResetId === row.enrollment_id ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => handleResetAttempts(row.enrollment_id)}
                                    disabled={resettingId === row.enrollment_id}
                                  >
                                    {resettingId === row.enrollment_id ? 'Azzeramento...' : 'Conferma'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setConfirmResetId(null)}
                                    disabled={resettingId === row.enrollment_id}
                                  >
                                    Annulla
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setConfirmResetId(row.enrollment_id)}
                                >
                                  Azzera tentativi
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowAttemptsModal(false)}>
            Chiudi
          </Button>
        </ModalFooter>
      </Modal>

      {/* Quiz Editor Modal */}
      <Modal
        isOpen={showQuizModal}
        onClose={() => setShowQuizModal(false)}
        title="Gestione Quiz"
        size="xl"
      >
        <ModalBody>
          {loadingQuiz ? (
            <div className="py-8 text-center">
              <InlineSpinner message="Caricamento quiz..." />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Quiz Settings */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-gray-900">Impostazioni Quiz</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titolo
                    </label>
                    <input
                      type="text"
                      value={quizForm.title || ''}
                      onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Titolo del quiz"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Punteggio minimo (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={quizForm.passing_score || 60}
                      onChange={(e) => setQuizForm({ ...quizForm, passing_score: parseInt(e.target.value) || 60 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Limite di tempo (minuti)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={quizForm.time_limit_minutes || ''}
                      onChange={(e) => setQuizForm({ ...quizForm, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Nessun limite"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tentativi massimi
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quizForm.max_attempts || 3}
                      onChange={(e) => setQuizForm({ ...quizForm, max_attempts: parseInt(e.target.value) || 3 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quizForm.shuffle_questions || false}
                      onChange={(e) => setQuizForm({ ...quizForm, shuffle_questions: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Mescola domande</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={quizForm.show_correct_answers !== false}
                      onChange={(e) => setQuizForm({ ...quizForm, show_correct_answers: e.target.checked })}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Mostra risposte corrette</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2">
                  {quiz && (
                    <Button variant="danger" onClick={handleDeleteQuiz}>
                      Elimina Quiz
                    </Button>
                  )}
                  <Button onClick={handleSaveQuiz} disabled={savingQuiz}>
                    {savingQuiz ? 'Salvataggio...' : quiz ? 'Salva Impostazioni' : 'Crea Quiz'}
                  </Button>
                </div>
              </div>

              {/* Questions Section - only show if quiz exists */}
              {quiz && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">
                      Domande ({quiz.questions?.length || 0})
                    </h3>
                    <Button onClick={handleOpenCreateQuestion}>
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Nuova Domanda
                    </Button>
                  </div>

                  {(!quiz.questions || quiz.questions.length === 0) ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <QuestionMarkCircleIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">Nessuna domanda creata</p>
                      <button
                        onClick={handleOpenCreateQuestion}
                        className="mt-2 text-primary-600 hover:underline"
                      >
                        + Aggiungi la prima domanda
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {quiz.questions.map((question, idx) => (
                        <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <span className="text-sm font-medium text-gray-500">
                                Domanda {idx + 1}
                                <Badge variant="default" size="sm" className="ml-2">
                                  {question.type === 'single' ? 'Scelta singola' :
                                   question.type === 'multiple' ? 'Scelta multipla' :
                                   question.type === 'true_false' ? 'Vero/Falso' : 'Testo'}
                                </Badge>
                                <span className="ml-2 text-gray-400">({question.points} pt)</span>
                              </span>
                              <p className="font-medium mt-1">{question.text}</p>
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleOpenEditQuestion(question)}
                                className="p-1 text-gray-500 hover:bg-gray-200 rounded"
                                title="Modifica domanda"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteQuestion(question.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                title="Elimina domanda"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          {/* Answers */}
                          <div className="ml-4 space-y-2">
                            {question.answers.map((answer) => (
                              <div
                                key={answer.id}
                                className={`flex items-center gap-2 p-2 rounded ${
                                  answer.is_correct ? 'bg-green-50 border border-green-200' : 'bg-gray-50'
                                }`}
                              >
                                <button
                                  onClick={() => handleToggleCorrectAnswer(answer)}
                                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                    answer.is_correct
                                      ? 'border-green-500 bg-green-500 text-white'
                                      : 'border-gray-300 hover:border-green-400'
                                  }`}
                                  title={answer.is_correct ? 'Risposta corretta' : 'Imposta come corretta'}
                                >
                                  {answer.is_correct && (
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </button>
                                <span className="flex-1 text-sm">{answer.text}</span>
                                <button
                                  onClick={() => handleDeleteAnswer(answer.id)}
                                  className="p-1 text-red-500 hover:bg-red-100 rounded opacity-50 hover:opacity-100"
                                  title="Elimina risposta"
                                >
                                  <TrashIcon className="h-3 w-3" />
                                </button>
                              </div>
                            ))}

                            {/* Add answer inline */}
                            <div className="flex gap-2 mt-2">
                              <input
                                type="text"
                                value={newAnswerText}
                                onChange={(e) => setNewAnswerText(e.target.value)}
                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500"
                                placeholder="Nuova risposta..."
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleAddAnswer(question.id)
                                  }
                                }}
                              />
                              <Button
                                size="sm"
                                onClick={() => handleAddAnswer(question.id)}
                                disabled={savingAnswer || !newAnswerText.trim()}
                              >
                                Aggiungi
                              </Button>
                            </div>
                          </div>

                          {question.explanation && (
                            <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-700">
                              <strong>Spiegazione:</strong> {question.explanation}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={() => setShowQuizModal(false)}>
            Chiudi
          </Button>
        </ModalFooter>
      </Modal>

      {/* Question Editor Modal */}
      <Modal
        isOpen={showQuestionModal}
        onClose={handleCloseQuestionModal}
        title={editingQuestion ? 'Modifica Domanda' : 'Nuova Domanda'}
        size="lg"
      >
        <ModalBody>
          <div className="space-y-4">
            {/* Question text */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Testo della domanda *
              </label>
              <textarea
                value={questionForm.text}
                onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                rows={3}
                placeholder="Inserisci la domanda..."
              />
            </div>

            {/* Question settings */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo di risposta
                </label>
                <select
                  value={questionForm.type}
                  onChange={(e) => setQuestionForm({ ...questionForm, type: e.target.value as 'single' | 'multiple' | 'text' | 'true_false' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="single">Scelta singola</option>
                  <option value="multiple">Scelta multipla</option>
                  <option value="true_false">Vero/Falso</option>
                  <option value="text">Risposta testuale</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Punti
                </label>
                <input
                  type="number"
                  min="1"
                  value={questionForm.points || 1}
                  onChange={(e) => setQuestionForm({ ...questionForm, points: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Save question button - only show if not saved yet */}
            {!currentQuestionId && (
              <div className="flex justify-end">
                <Button
                  onClick={handleSaveQuestion}
                  disabled={savingQuestion || !questionForm.text.trim()}
                >
                  {savingQuestion ? 'Salvataggio...' : 'Salva Domanda'}
                </Button>
              </div>
            )}

            {/* Update question button - show if editing */}
            {currentQuestionId && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleSaveQuestion}
                  disabled={savingQuestion || !questionForm.text.trim()}
                >
                  {savingQuestion ? 'Aggiornamento...' : 'Aggiorna Domanda'}
                </Button>
              </div>
            )}

            {/* Answers section - only show after question is saved */}
            {currentQuestionId && (
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  Risposte ({questionAnswers.length})
                  {questionForm.type === 'multiple' && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      (seleziona tutte le risposte corrette)
                    </span>
                  )}
                  {questionForm.type === 'single' && (
                    <span className="text-sm text-gray-500 font-normal ml-2">
                      (seleziona la risposta corretta)
                    </span>
                  )}
                </h4>

                {/* Existing answers */}
                <div className="space-y-2 mb-4">
                  {questionAnswers.map((answer) => (
                    <div
                      key={answer.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        answer.is_correct
                          ? 'bg-green-50 border-green-300'
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggleAnswerCorrectInModal(answer)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          answer.is_correct
                            ? 'border-green-500 bg-green-500 text-white'
                            : 'border-gray-300 hover:border-green-400'
                        }`}
                        title={answer.is_correct ? 'Risposta corretta - clicca per togliere' : 'Clicca per impostare come corretta'}
                      >
                        {answer.is_correct && (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                      <span className={`flex-1 ${answer.is_correct ? 'text-green-800 font-medium' : 'text-gray-700'}`}>
                        {answer.text}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteAnswerInModal(answer.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded"
                        title="Elimina risposta"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add new answer */}
                <div className="flex gap-2 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={newAnswerText}
                      onChange={(e) => setNewAnswerText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      placeholder="Testo della risposta..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newAnswerText.trim()) {
                          e.preventDefault()
                          handleAddAnswerInModal()
                        }
                      }}
                    />
                  </div>
                  <label className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={newAnswerCorrect}
                      onChange={(e) => setNewAnswerCorrect(e.target.checked)}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700 whitespace-nowrap">Corretta</span>
                  </label>
                  <Button
                    onClick={handleAddAnswerInModal}
                    disabled={savingAnswer || !newAnswerText.trim()}
                  >
                    {savingAnswer ? '...' : 'Aggiungi'}
                  </Button>
                </div>

                {questionAnswers.length === 0 && (
                  <p className="text-sm text-amber-600 mt-2">
                    Aggiungi almeno una risposta alla domanda
                  </p>
                )}
                {questionAnswers.length > 0 && !questionAnswers.some(a => a.is_correct) && (
                  <p className="text-sm text-amber-600 mt-2">
                    Seleziona almeno una risposta corretta
                  </p>
                )}
              </div>
            )}

            {/* Explanation - show after question is saved */}
            {currentQuestionId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spiegazione (mostrata dopo la risposta)
                </label>
                <textarea
                  value={questionForm.explanation || ''}
                  onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  placeholder="Opzionale: spiega perche questa e la risposta corretta..."
                />
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleCloseQuestionModal}>
            {currentQuestionId ? 'Chiudi' : 'Annulla'}
          </Button>
          {currentQuestionId && (
            <Button onClick={handleOpenCreateQuestion}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Nuova Domanda
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  )
}
